import { BarChart3, Briefcase, CheckCircle2, FileCheck2, Shield, TrendingUp } from 'lucide-react';

type EnterpriseProofPackProps = {
  compact?: boolean;
};

const marketSignals = [
  {
    stat: '$8.8T',
    headline: 'Annual productivity drag from low engagement worldwide.',
    sourceLabel: 'Gallup - State of the Global Workplace 2023',
    sourceUrl: 'https://www.gallup.com/workplace/393497/employee-engagement-drives-growth.aspx',
  },
  {
    stat: '$1T',
    headline: 'Annual lost productivity from depression and anxiety.',
    sourceLabel: 'WHO - Mental health at work',
    sourceUrl: 'https://www.who.int/news-room/fact-sheets/detail/mental-health-at-work',
  },
  {
    stat: '12B days',
    headline: 'Working days lost each year to depression/anxiety.',
    sourceLabel: 'WHO - Mental health at work',
    sourceUrl: 'https://www.who.int/news-room/fact-sheets/detail/mental-health-at-work',
  },
  {
    stat: '15 cigarettes/day',
    headline: 'Mortality impact comparison for social isolation.',
    sourceLabel: 'U.S. HHS - Surgeon General Advisory (2023)',
    sourceUrl:
      'https://www.hhs.gov/about/news/2023/05/03/new-surgeon-general-advisory-raises-alarm-about-devastating-impact-epidemic-loneliness-isolation-united-states.html',
  },
];

const differentiation = [
  {
    title: 'Not another pulse survey',
    body: 'TENSION tracks trend movement and relationship strain intelligence continuously, instead of periodic sentiment snapshots.',
  },
  {
    title: 'Not just reactive support',
    body: 'TENSION is designed for earlier intervention before workplace damage appears in absenteeism, disengagement, and attrition.',
  },
  {
    title: 'Not a privacy tradeoff',
    body: 'TENSION separates individual privacy from leadership-level analytics, so organizations get visibility without exposing private details.',
  },
];

const implementationTimeline = [
  'Week 1-2: Buyer alignment, data governance framing, enterprise success metrics definition',
  'Week 3-4: Tenant setup, role mapping, stakeholder onboarding, launch communications',
  'Week 5-8: Pilot rollout, baseline collection, intervention playbook activation',
  'Week 9-12: Executive readout with trend movement, ROI narrative, and scale recommendation',
];

const procurementFaq = [
  'How do you protect individual privacy while still providing meaningful cohort-level visibility?',
  'What role-scoped controls exist for HR, clinical partners, leadership, and admins?',
  'How quickly can we stand up a pilot and generate an executive-quality baseline report?',
  'How does TENSION integrate with existing wellbeing and support pathways?',
];

export function EnterpriseProofPack({ compact = false }: EnterpriseProofPackProps) {
  return (
    <section className="enterprise-surface-card rounded-2xl p-6 text-slate-100 md:p-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="enterprise-meta-pill">
            <BarChart3 className="h-3.5 w-3.5" aria-hidden />
            Market-validated enterprise case
          </p>
          <h2 className="mt-3 text-2xl font-black tracking-tight text-slate-100 md:text-3xl">
            Why TENSION for Work is positioned to define a new enterprise category
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-300 md:text-base">
            This is built as a relationship-risk intelligence system for employers and care ecosystems, not a generic wellness tool.
            The model combines measurable business impact with privacy-first operating standards to support both procurement and adoption.
          </p>
        </div>
      </div>

      <div className="mt-6 grid gap-3 md:grid-cols-2">
        {marketSignals.map((item) => (
          <div key={item.stat} className="rounded-xl border border-slate-700 bg-slate-900/70 p-4">
            <p className="text-2xl font-black text-slate-100">{item.stat}</p>
            <p className="mt-1 text-sm text-slate-300">{item.headline}</p>
            <a
              href={item.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-block text-xs font-semibold text-blue-300 hover:underline"
            >
              Source: {item.sourceLabel}
            </a>
          </div>
        ))}
      </div>

      {!compact && (
        <>
          <div className="mt-6 grid gap-3 md:grid-cols-3">
            {differentiation.map((item) => (
              <div key={item.title} className="rounded-xl border border-slate-700 bg-slate-900/70 p-4">
                <p className="text-sm font-semibold text-slate-100">{item.title}</p>
                <p className="mt-1 text-sm text-slate-300">{item.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-4">
              <p className="flex items-center gap-2 text-sm font-semibold text-slate-100">
                <TrendingUp className="h-4 w-4 text-blue-300" aria-hidden />
                ROI framing for enterprise buyers
              </p>
              <ul className="mt-2 space-y-2 text-sm text-slate-300">
                <li className="flex gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" aria-hidden />
                  <span>Model baseline impact using retention risk movement and manager load reduction.</span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" aria-hidden />
                  <span>Track adoption velocity by cohort, function, and intervention participation.</span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" aria-hidden />
                  <span>Produce executive readouts that tie risk trend movement to business outcomes.</span>
                </li>
              </ul>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-4">
              <p className="flex items-center gap-2 text-sm font-semibold text-slate-100">
                <Shield className="h-4 w-4 text-blue-300" aria-hidden />
                Compliance and governance matrix
              </p>
              <ul className="mt-2 space-y-2 text-sm text-slate-300">
                <li className="flex gap-2">
                  <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-blue-300" aria-hidden />
                  <span>Role-scoped access boundaries for admin, HR, leadership, and partner stakeholders.</span>
                </li>
                <li className="flex gap-2">
                  <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-blue-300" aria-hidden />
                  <span>Domain and identity controls with environment-level policy management.</span>
                </li>
                <li className="flex gap-2">
                  <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-blue-300" aria-hidden />
                  <span>Audit-aware operating model for procurement and legal review workflows.</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-4">
              <p className="flex items-center gap-2 text-sm font-semibold text-slate-100">
                <Briefcase className="h-4 w-4 text-blue-300" aria-hidden />
                90-day implementation blueprint
              </p>
              <ul className="mt-2 space-y-2 text-sm text-slate-300">
                {implementationTimeline.map((step) => (
                  <li key={step} className="flex gap-2">
                    <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-blue-300" />
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-4">
              <p className="flex items-center gap-2 text-sm font-semibold text-slate-100">
                <FileCheck2 className="h-4 w-4 text-blue-300" aria-hidden />
                Procurement FAQ to accelerate deal cycles
              </p>
              <ul className="mt-2 space-y-2 text-sm text-slate-300">
                {procurementFaq.map((question) => (
                  <li key={question} className="flex gap-2">
                    <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-blue-300" />
                    <span>{question}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
